import Chip from '@mui/material/Chip';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import PanToolOutlinedIcon from '@mui/icons-material/PanToolOutlined';
import TroubleshootOutlinedIcon from '@mui/icons-material/TroubleshootOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { Reveal } from '@/components/Reveal';

type AiCapability = {
  icon: SvgIconComponent;
  title: string;
  body: string;
  tag?: string;
};

const capabilities: AiCapability[] = [
  {
    icon: AutoFixHighOutlinedIcon,
    title: 'Autonomous resolution',
    body: 'Routine, non-destructive tickets — printer offline, password reset, everyday troubleshooting — get diagnosed, fixed, verified, and closed by the agent. Today the fix lands for one-click approval; hands-off for these classes is where Nexus is headed.',
  },
  {
    icon: PanToolOutlinedIcon,
    title: 'A human owns anything destructive',
    body: 'Destructive or irreversible actions never go autonomous. The agent stops, packages the full context, and routes the approval to a person — including calling them. You decide; it executes.',
  },
  {
    icon: PhoneInTalkOutlinedIcon,
    title: 'Inbound & outbound AI calling',
    body: 'A voice agent that answers, triages, schedules, and places the escalation and approval calls — so a human is reached the moment one is needed, without your team watching a queue.',
    tag: 'On the roadmap',
  },
  {
    icon: TroubleshootOutlinedIcon,
    title: 'AI cyber analysis',
    body: 'Continuous posture scoring, phishing simulation, and breach/vulnerability analysis — prioritized by AI so your team acts on what actually matters first.',
  },
];

export function AgenticAI(): JSX.Element {
  return (
    <section id="ai" className="border-y border-white/10 bg-white/[0.015] py-16 md:py-24">
      <div className="container-content">
        <Reveal>
          <div className="mx-auto mb-14 max-w-2xl text-center">
            <Chip
              label="The AI layer"
              size="small"
              className="mb-4 border border-nexus-purple/30 bg-nexus-purple/[0.14] font-semibold text-nexus-purple-light"
            />
            <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
              Autonomous resolution. Creation to close.
            </h2>
            <p className="mt-4 text-white/70">
              The whole ticket lifecycle is agentic — created, triaged, diagnosed, and resolved by
              AI. The trajectory is full autonomy for routine, non-destructive work; a human always
              stays in the loop for anything destructive or irreversible, and the agent calls them
              when it needs a decision.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {capabilities.map((c, i) => {
            const Icon = c.icon;
            return (
              <Reveal key={c.title} delay={i * 80}>
                <div className="group h-full rounded-2xl border border-white/10 bg-white/[0.02] p-6 transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50">
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light transition-transform duration-200 group-hover:scale-110">
                    <Icon fontSize="small" />
                  </div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-[17px] font-semibold text-nexus-ink">{c.title}</h3>
                    {c.tag && (
                      <Chip
                        label={c.tag}
                        size="small"
                        className="h-[18px] border border-white/15 bg-transparent text-[9.5px] font-semibold text-white/55"
                      />
                    )}
                  </div>
                  <p className="mt-1.5 text-sm text-white/65">{c.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <p className="mt-8 text-center text-xs text-white/40">
          Honest about where this is: today, autonomous resolutions land for one-click approval, and
          anything destructive or irreversible always requires a human. Autonomy ships class by class —
          each capability labeled by what&apos;s true today versus what&apos;s on the roadmap.
        </p>
      </div>
    </section>
  );
}
